import React, { useState, useEffect, useMemo } from 'react';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';
import { useShippingWorker } from '../hooks/useShippingWorker';

interface PackagingCharge {
  minAmount: number;
  maxAmount: number;
  charge: number;
  _id: string;
  id: string;
}

interface CompanyData {
  companyName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  shippingSettings?: {
    packagingCharges?: PackagingCharge[];
  };
}

interface PickupLocation {
  id: string;
  name: string;
  address: string;
  landmark?: string;
  area?: string;
  city: string;
  state: string;
  zipCode?: string;
  pincode?: string;
  phone: string;
  email?: string;
  timings?: string;
  mapUrl?: string;
  isActive: boolean;
  distance?: number;
  transportProviderName?: string;
}

const Shipping: React.FC = () => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  const { searchLocations, filterLocations, isWorkerSupported } = useShippingWorker();

  useEffect(() => {
    const loadData = async () => {
      // Load company info
      try {
        const data = await apiRequest<CompanyData>(API_ENDPOINTS.COMPANY.PUBLIC);
        setCompanyData(data);
      } catch (error) {
        console.error('Error loading company info:', error);
      } finally {
        setLoading(false);
      }

      // Load pickup locations
      try {
        const locations = await apiRequest<PickupLocation[]>(API_ENDPOINTS.COMPANY.PICKUP_LOCATIONS);
        
        if (Array.isArray(locations)) {
          setPickupLocations(locations);
        } else {
          console.error('Invalid pickup locations response:', locations);
          setPickupLocations([]);
        }
      } catch (error) {
        console.error('Error loading pickup locations:', error);
        setPickupLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    loadData();
  }, []);

  // Get unique cities and states for filters
  const { cities, states } = useMemo(() => {
    const citySet = new Set<string>();
    const stateSet = new Set<string>();
    
    pickupLocations.forEach(location => {
      if (location.city) citySet.add(location.city);
      if (location.state) stateSet.add(location.state);
    });
    
    return {
      cities: Array.from(citySet).sort(),
      states: Array.from(stateSet).sort()
    };
  }, [pickupLocations]);

  // Filter and search locations using web worker
  const [filteredLocations, setFilteredLocations] = useState<PickupLocation[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const processLocations = async () => {
      // Skip if no locations loaded yet
      if (pickupLocations.length === 0) {
        setFilteredLocations([]);
        return;
      }
      
      setIsProcessing(true);
      
      try {
        // Start with all locations (don't filter by isActive here, let the data show all)
        let results = [...pickupLocations];
        
        // Apply city/state filters if selected
        if (selectedCity || selectedState) {
          results = await filterLocations(results, {
            city: selectedCity,
            state: selectedState
          });
        }
        
        // Apply search if term exists
        if (searchTerm.trim()) {
          results = await searchLocations(results, searchTerm);
        }
        
        setFilteredLocations(results);
        setCurrentPage(1); // Reset to first page when filters change
      } catch (error) {
        console.error('Error processing locations:', error);
        setFilteredLocations(pickupLocations);
      } finally {
        setIsProcessing(false);
      }
    };
    
    processLocations();
  }, [pickupLocations, searchTerm, selectedCity, selectedState, searchLocations, filterLocations]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredLocations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLocations = filteredLocations.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of locations section
    window.scrollTo({ top: 200, behavior: 'smooth' });
  };

  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Shipping Information
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Secure and reliable delivery of your fireworks across India through lorry services
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            {/* Pickup Points */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                <span className="mr-3">📍</span>
                Pickup Points
              </h2>
              
              {/* Search and Filter Section */}
              {pickupLocations.length > 0 && (
                <div className="mb-6 space-y-4">
                  {/* Search Bar */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by name, address, city, landmark, phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-3 pr-10 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-all"
                    />
                    <svg
                      className="absolute right-3 top-3.5 h-5 w-5 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  
                  {/* Filters */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <select
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      className="flex-1 px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                    >
                      <option value="">All States</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    
                    <select
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                      className="flex-1 px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                    >
                      <option value="">All Cities</option>
                      {cities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                    
                    {(searchTerm || selectedCity || selectedState) && (
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setSelectedCity('');
                          setSelectedState('');
                        }}
                        className="px-4 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>
                  
                  {/* Results Info */}
                  {!isProcessing && filteredLocations.length > 0 && (
                    <div className="text-sm text-neutral-600">
                      {(searchTerm || selectedCity || selectedState) ? (
                        <>
                          Found {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''}
                          {searchTerm && ` matching "${searchTerm}"`}
                          {selectedState && ` in ${selectedState}`}
                          {selectedCity && `, ${selectedCity}`}
                        </>
                      ) : (
                        <>Total {filteredLocations.length} pickup location{filteredLocations.length !== 1 ? 's' : ''}</>
                      )}
                      {filteredLocations.length > itemsPerPage && (
                        <span className="ml-2 text-accent-600 font-medium">(Page {currentPage} of {totalPages})</span>
                      )}
                    </div>
                  )}
                  
                  {/* Web Worker Status */}
                  {!isWorkerSupported && (
                    <div className="text-xs text-neutral-500 bg-neutral-100 rounded px-3 py-1 inline-block">
                      Search running in compatibility mode
                    </div>
                  )}
                </div>
              )}
              
              {locationsLoading ? (
                <div className="text-center py-8">
                  <span className="text-neutral-500">Loading pickup locations...</span>
                </div>
              ) : isProcessing ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
                  <p className="mt-2 text-neutral-500">Searching locations...</p>
                </div>
              ) : filteredLocations.length > 0 ? (
                  <>
                    <div className="grid md:grid-cols-2 gap-6">
                      {paginatedLocations.map((location) => (
                    <div key={location.id} className={`rounded-xl p-6 ${location.isActive ? 'bg-neutral-50' : 'bg-gray-100 opacity-75'}`}>
                      <div className="flex items-start justify-between">
                        <h3 className="text-lg font-semibold text-neutral-900 mb-3">{location.name}</h3>
                        {!location.isActive && (
                          <span className="text-xs px-2 py-1 bg-gray-500 text-white rounded-full">Inactive</span>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <span className="text-neutral-500 mr-2">📍</span>
                          <div className="flex-1">
                            <p className="text-neutral-700">{location.address}</p>
                            {location.landmark && (
                              <p className="text-sm text-neutral-600">Near: {location.landmark}</p>
                            )}
                            {location.area && (
                              <p className="text-sm text-neutral-600">{location.area}, {location.city}</p>
                            )}
                            <p className="text-sm text-neutral-600">{location.city}, {location.state} - {location.zipCode || location.pincode}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span className="text-neutral-500 mr-2">📞</span>
                          <span className="text-neutral-700">{location.phone}</span>
                        </div>
                        {location.email && (
                          <div className="flex items-center">
                            <span className="text-neutral-500 mr-2">✉️</span>
                            <span className="text-neutral-700">{location.email}</span>
                          </div>
                        )}
                        {location.timings && (
                          <div className="flex items-start">
                            <span className="text-neutral-500 mr-2">🕐</span>
                            <div className="flex-1">
                              <p className="text-sm text-neutral-700">{location.timings}</p>
                            </div>
                          </div>
                        )}
                        {location.transportProviderName && (
                          <div className="flex items-center">
                            <span className="text-neutral-500 mr-2">🚚</span>
                            <span className="text-sm text-neutral-700">{location.transportProviderName}</span>
                          </div>
                        )}
                      </div>
                      {location.mapUrl && (
                        <a 
                          href={location.mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center mt-4 text-accent-600 hover:text-accent-700 font-medium text-sm"
                        >
                          <span className="mr-1">🗺️</span>
                          View on Map
                        </a>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-8 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="text-sm text-neutral-600">
                        Showing <span className="font-semibold">{startIndex + 1}-{Math.min(endIndex, filteredLocations.length)}</span> of <span className="font-semibold">{filteredLocations.length}</span> locations
                      </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                          currentPage === 1
                            ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                            : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                        }`}
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          if (pageNum > totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`w-8 h-8 rounded-lg font-medium transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-accent-600 text-white'
                                  : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded-lg font-medium transition-colors ${
                          currentPage === totalPages
                            ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                            : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                    </div>
                  </div>
                )}
                </>
                ) : (
                  <div className="bg-neutral-100 rounded-xl p-8 text-center">
                    <p className="text-neutral-600 font-medium">No pickup locations found</p>
                    <p className="text-sm text-neutral-500 mt-2">
                      {searchTerm && `No locations match "${searchTerm}". `}
                      Try adjusting your search or filters.
                    </p>
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCity('');
                        setSelectedState('');
                      }}
                      className="mt-4 px-4 py-2 text-sm font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              
              {/* Show main warehouse if no pickup locations */}
              {pickupLocations.length === 0 && companyData && (
                <div className="bg-neutral-50 rounded-xl p-6 mt-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Main Warehouse</h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="text-neutral-500 mr-2">📍</span>
                      <div className="flex-1">
                        <p className="text-neutral-700">{companyData.address.street}</p>
                        <p className="text-sm text-neutral-600">{companyData.address.city}, {companyData.address.state} - {companyData.address.zipCode}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-neutral-500 mr-2">📞</span>
                      <span className="text-neutral-700">{companyData.phone}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-neutral-500 mr-2">✉️</span>
                      <span className="text-neutral-700">{companyData.email}</span>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Delivery & Packaging Fees */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                <span className="mr-3">💰</span>
                Delivery & Packaging Fees
              </h2>
              
              {/* Delivery Fee Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-2 flex items-center">
                  <span className="mr-2">🚚</span>
                  Delivery Charges
                </h3>
                <p className="text-neutral-700">
                  <strong>Important:</strong> Delivery fees are decided by the lorry service provider based on:
                </p>
                <ul className="mt-3 space-y-2 text-neutral-600">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>Distance from our warehouse to your location</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>Total weight and volume of your order</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>Special handling requirements for fireworks</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>Current fuel prices and transportation costs</span>
                  </li>
                </ul>
                <p className="text-neutral-700 mt-3">
                  The exact delivery charge will be communicated to you before confirming your order.
                </p>
              </div>

              {/* Packaging Charges */}
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                  <span className="mr-2">📦</span>
                  Packaging Charges
                </h3>
                {loading ? (
                  <div className="text-center py-4">
                    <span className="text-neutral-500">Loading packaging charges...</span>
                  </div>
                ) : companyData?.shippingSettings?.packagingCharges && companyData.shippingSettings.packagingCharges.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-neutral-100">
                          <th className="text-left p-4 font-semibold text-neutral-900">Order Value</th>
                          <th className="text-left p-4 font-semibold text-neutral-900">Packaging Charge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyData.shippingSettings.packagingCharges
                          .sort((a, b) => a.minAmount - b.minAmount)
                          .map((charge, index) => (
                            <tr key={index} className="border-b border-neutral-200">
                              <td className="p-4 text-neutral-700">
                                {charge.minAmount === 0 && charge.maxAmount === 0 
                                  ? 'All Orders' 
                                  : charge.maxAmount === 0 
                                    ? `₹${charge.minAmount.toLocaleString('en-IN')} & above`
                                    : `₹${charge.minAmount.toLocaleString('en-IN')} - ₹${charge.maxAmount.toLocaleString('en-IN')}`}
                              </td>
                              <td className="p-4 text-neutral-700 font-medium">
                                ₹{charge.charge.toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    <p className="text-sm text-neutral-600 mt-3">
                      * Packaging charges are added to ensure safe delivery of fireworks with proper cushioning and protection
                    </p>
                  </div>
                ) : (
                  <div className="bg-neutral-100 rounded-lg p-4 text-center text-neutral-600">
                    Contact us for packaging charge details
                  </div>
                )}
              </div>
            </section>

            {/* Special Handling */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                <span className="mr-3">🎆</span>
                Special Handling for Fireworks
              </h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-3 mt-1">⚠️</span>
                    <span className="text-neutral-700">All fireworks are shipped with special safety packaging and handling protocols</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-3 mt-1">🚚</span>
                    <span className="text-neutral-700">Delivery by trained personnel who understand fireworks safety requirements</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-3 mt-1">📄</span>
                    <span className="text-neutral-700">All shipments include safety instructions and legal compliance documents</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-yellow-600 mr-3 mt-1">🏠</span>
                    <span className="text-neutral-700">Adult signature required for delivery (18+ years)</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Tracking */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                <span className="mr-3">📍</span>
                Order Tracking
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Real-time Updates</h3>
                  <ul className="space-y-2 text-neutral-600">
                    <li>• SMS notifications at every stage</li>
                    <li>• Email updates with tracking details</li>
                    <li>• Live tracking through our website</li>
                    <li>• Customer service support</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Delivery Process</h3>
                  <ul className="space-y-2 text-neutral-600">
                    <li>• Order confirmation within 2 hours</li>
                    <li>• Quality check and packaging</li>
                    <li>• Dispatch notification</li>
                    <li>• Out for delivery alert</li>
                    <li>• Delivery confirmation</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-accent-50 rounded-xl p-6">
              <h2 className="text-xl font-bold text-neutral-900 mb-3">Need Help with Your Order?</h2>
              <p className="text-neutral-700 mb-4">
                Our customer service team is available to assist you with any shipping queries.
              </p>
              {companyData && (
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center">
                    <span className="mr-2">📞</span>
                    <span className="text-neutral-700">{companyData.phone}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="mr-2">✉️</span>
                    <span className="text-neutral-700">{companyData.email}</span>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shipping;